import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';

// Import types
import { Seeker, Recruiter, SeekerFormData, RecruiterFormData, Notification, PAGE_LIMIT } from './types';

// Import mock data - only for recruiters now
import { INITIAL_RECRUITERS } from './Data/mockData';
import PublicLayout from './layout/PublicLayout';
import LandingPage from './pages/LandingPage';
import SeekerStatus from './pages/SeekerStatus';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import SeekerRegistration from './pages/SeekerRegistration';
import RecruiterRegistration from './pages/RecruiterRegistration';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// API base URL
const API_BASE_URL = 'https://wehireindia.com/wehire_api';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Main App Component with Router
function App() {
  return (
    <AppContent />
  );
}

// AppContent component that uses router hooks
function AppContent() {
  const navigate = useNavigate();

  // --- Global State ---
  const [seekers, setSeekers] = useState<Seeker[]>([]);
  const [recruiters, setRecruiters] = useState<Recruiter[]>(([]));

  const [seekerFilter, setSeekerFilter] = useState<string | null>(null)
  const [recruiterFilter, setRecruiterFilter] = useState<string | null>(null)

  const [seekerPage, setSeekerPage] = useState<number>(1);
  const [seekerTotalPages, setSeekerTotalPages] = useState<number>(1);
  const [seekerTotal, setSeekerTotal] = useState<number>(0);

  const [recruiterPage, setRecruiterPage] = useState<number>(1);
  const [recruiterTotalPages, setRecruiterTotalPages] = useState<number>(1);
  const [recruiterTotal, setRecruiterTotal] = useState<number>(0);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return false;

    const expiresAt = localStorage.getItem('token_expires_at');
    if (expiresAt) {
      const now = new Date();
      const expirationDate = new Date(expiresAt);
      return now < expirationDate;
    }
    return true;
  });
  const [notification, setNotification] = useState<Notification | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Theme Toggle ---
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, []);

  // --- Set up axios interceptor for authentication ---
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('token_expires_at');
          setIsAdminLoggedIn(false);
          navigate('/');
          showNotification("Session expired. Please login again.", "pending");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate]);

  const handleSeekerPageChange = (page: number) => {
    if (page < 1 || page > seekerTotalPages) return;
    fetchSeekers(page);
  };

  const handleRecruiterPageChange = (page: number) => {
    if (page < 1 || page > recruiterTotalPages) return;
    fetchRecruiters(page);
  };

  // --- Fetch Seekers from API ---
  const fetchSeekers = async (page = 1, searchString = seekerFilter) => {
    try {
      const response = await axios.get('/seekers', {
        params: {
          page,
          limit: PAGE_LIMIT,
          searchString: searchString || undefined
        }
      });

      const apiSeekers: Seeker[] = response.data.data.map((s: any) => ({
        id: s.id,
        name: s.name,
        email: s.email,
        role: s.role,
        exp: s.exp,
        location: s.location,
        status: s.status,
        phone: s.phone
      }));

      setSeekers(apiSeekers);
      setSeekerPage(response.data.meta.page);
      setSeekerTotalPages(response.data.meta.totalPages);
      setSeekerTotal(response.data.meta.total);
    } catch (err) {
      setError('Failed to load seekers');
    } finally {
      setLoading(false);
    }
  };

  // --- Fetch Recruiters from API ---
  const fetchRecruiters = async (page = 1, searchString = recruiterFilter) => {
    try {
      const response = await axios.get('/recruiters', {
        params: {
          page,
          limit: PAGE_LIMIT,
          searchString: searchString || undefined
        }
      });

      const apiRecruiters: Recruiter[] = response.data.data.map((r: any) => ({
        id: r.id,
        company: r.company,
        recruiter: r.recruiter,
        email: r.email,
        role: r.role,
        status: r.status
      }));

      setRecruiters(apiRecruiters);
      setRecruiterPage(response.data.meta.page);
      setRecruiterTotalPages(response.data.meta.totalPages);
      setRecruiterTotal(response.data.meta.total);
    } catch (err) {
      console.error('Failed to load recruiters');
    }
  };


  const handleSeekerFilter = (status: string) => {
    if (seekerFilter === status) {
      // same card clicked again → remove filter
      setSeekerFilter(null)
    } else {
      setSeekerFilter(status)
    }
  }

  const handleRecruiterFilter = (status: string) => {
    if (recruiterFilter === status) {
      setRecruiterFilter(null)
    } else {
      setRecruiterFilter(status)
    }
  }

  // Helper function to map API status to your local status types
  const mapApiStatusToLocal = (apiStatus?: string): Seeker['status'] => {
    if (!apiStatus) return 'Submitted';

    const statusMap: Record<string, Seeker['status']> = {
      submitted: 'Submitted',
      reviewed: 'Reviewed',
      rejected: 'Rejected',
      hired: 'Hired',
      pending: 'Submitted'
    };

    return statusMap[apiStatus.toLowerCase()] || 'Submitted';
  };

  const mapRecruiterApiStatusToLocal = (apiStatus: string): Recruiter['status'] => {
    const statusMap: Record<string, Recruiter['status']> = {
      'approved': 'Approved',
      'pending': 'Pending',
      'blocked': 'Blocked',
      'rejected': 'Blocked'
    };

    return statusMap[apiStatus?.toLowerCase()] || 'Pending';
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // --- Handlers ---
  const showNotification = (msg: string, type: 'success' | 'pending' = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleAdminLogin = () => {
    setIsAdminLoggedIn(true);
    navigate('/admin/seekers');
    // Fetch fresh data after login
    fetchSeekers();
    fetchRecruiters();
  };

  const refreshData = () => {
    fetchSeekers(seekerPage);
    fetchRecruiters(recruiterPage);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('token_expires_at');
    setIsAdminLoggedIn(false);
    navigate('/');
    showNotification("Logged out successfully");
  };

  const handleSeekerRegister = async (data: SeekerFormData) => {
    try {
      const response = await axios.post('/seekers/register', data);

      const newSeeker: Seeker = {
        id: response.data.id || Date.now(),
        name: data.name,
        email: data.email,
        role: data.role,
        exp: data.exp,
        location: data.location,
        status: 'Submitted',
        phone: data.phone
      };

      setSeekers([newSeeker, ...seekers]);
      showNotification("Registration Successful!");
      navigate('/status');
    } catch (err) {
      console.error('Error registering seeker:', err);
      showNotification("Registration failed. Please try again.", "pending");
    }
  };

  const handleRecruiterRegister = (data: RecruiterFormData) => {
    const newRecruiter: Recruiter = { id: Date.now(), ...data, status: 'Pending' };
    setRecruiters([newRecruiter, ...recruiters]);
    showNotification("Waiting for Admin Approval", "pending");
    navigate('/');
  };

  const updateSeekerStatus = async (id: number, status: string) => {
    try {
      // Convert local status to API status if needed
      const apiStatus = status;
      await axios.patch(`/seekers/${id}/status`, { status: apiStatus });

      // Update local state
      setSeekers(seekers.map(s => s.id === id ? { ...s, status: status as Seeker['status'] } : s));
      showNotification(`Seeker status updated to ${status}`);
    } catch (err) {
      console.error('Error updating seeker status:', err);
      showNotification("Failed to update status", "pending");
    }
  };

  const updateRecruiterStatus = async (id: number, status: string) => {
    try {
      // Convert local status to API status if needed
      const apiStatus = status;
      await axios.patch(`/recruiters/${id}/status`, { status: apiStatus });

      // Update local state
      setRecruiters(recruiters.map(r => r.id === id ? { ...r, status: status as Recruiter['status'] } : r));
      showNotification(`Recruiter status updated to ${status}`);
    } catch (err) {
      console.error('Error updating recruiter status:', err);
      showNotification("Failed to update status", "pending");
    }
  };

  // --- Initial data fetch ---
  useEffect(() => {
    if (isAdminLoggedIn) {
      fetchSeekers();
    }
  }, [isAdminLoggedIn, seekerFilter]);

  useEffect(() => {
    if (isAdminLoggedIn) {
      fetchRecruiters();
    }
  }, [isAdminLoggedIn, recruiterFilter]);


  // Protected Admin Route component
  const ProtectedAdminRoute = ({ children }: { children: React.ReactNode }) => {
    if (!isAdminLoggedIn) {
      return <Navigate to="/admin/login" replace />;
    }
    return <>{children}</>;
  };

  return (
    <div className={theme}>
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        theme={theme === 'dark' ? 'dark' : 'light'}
      />
      {notification && (
        <div className={`fixed top-24 right-6 z-[60] px-6 py-4 rounded-2xl shadow-2xl text-white font-bold tracking-wide animate-bounce flex items-center gap-3 ${notification.type === 'pending' ? 'bg-amber-500 shadow-amber-500/40' : 'bg-emerald-500 shadow-emerald-500/40'}`}>
          {notification.type === 'pending' ? <Clock className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
          {notification.msg}
        </div>
      )}

      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <PublicLayout
            theme={theme}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            toggleTheme={toggleTheme}
            setTheme={setTheme}
          >
            <LandingPage theme={theme} />
          </PublicLayout>
        } />

        {/* <Route path="/apply" element={
          <PublicLayout
            theme={theme}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            toggleTheme={toggleTheme}
            setTheme={setTheme}
          >
            <SeekerRegistration onRegister={handleSeekerRegister} theme={theme} />
          </PublicLayout>
        } /> */}

<Route path="/apply" element={
  <div className="min-h-screen bg-[#f8f7ff]">
    <SeekerRegistration onRegister={handleSeekerRegister} theme={theme} />
  </div>
} />



        {/* <Route path="/recruiters" element={
          <PublicLayout
            theme={theme}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            toggleTheme={toggleTheme}
            setTheme={setTheme}
          >
            <RecruiterRegistration onRegister={handleRecruiterRegister} theme={theme} />
          </PublicLayout>
        } /> */}

<Route path="/recruiters" element={
  <div className="min-h-screen bg-[#f8f7ff]">
    <RecruiterRegistration onRegister={handleRecruiterRegister} theme={theme} />
  </div>
} />


        <Route path="/status" element={
          <PublicLayout
            theme={theme}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            toggleTheme={toggleTheme}
            setTheme={setTheme}
          >
            <SeekerStatus
              seekers={seekers}
              theme={theme}
              loading={loading}
              error={error || undefined}
            />
          </PublicLayout>
        } />

        {/* Admin Routes */}
        <Route path="/admin/login" element={
          <div className={theme === 'dark' ? 'dark' : ''}>
            <AdminLogin onLogin={handleAdminLogin} theme={theme} />
          </div>
        } />

       // In App.tsx, update your routes:
        <Route path="/admin" element={
          <ProtectedAdminRoute>
            <div className={theme === 'dark' ? 'dark' : ''}>
              <AdminDashboard
                seekers={seekers}
                recruiters={recruiters}
                updateSeekerStatus={updateSeekerStatus}
                updateRecruiterStatus={updateRecruiterStatus}
                logout={handleAdminLogout}
                refreshData={() => {
                  fetchSeekers(seekerPage);
                  fetchRecruiters(recruiterPage);
                }}
                loading={loading}
                seekerPage={seekerPage}
                seekerTotalPages={seekerTotalPages}
                seekerTotal={seekerTotal}
                recruiterPage={recruiterPage}
                recruiterTotalPages={recruiterTotalPages}
                recruiterTotal={recruiterTotal}
                onSeekerPageChange={handleSeekerPageChange}
                onRecruiterPageChange={handleRecruiterPageChange}
                onSeekerFilter={handleSeekerFilter}
                onRecruiterFilter={handleRecruiterFilter}
                seekerFilter={seekerFilter}
                recruiterFilter={recruiterFilter}
              />
            </div>
          </ProtectedAdminRoute>
        } />

        <Route path="/admin/seekers" element={
          <ProtectedAdminRoute>
            <div className={theme === 'dark' ? 'dark' : ''}>
              <AdminDashboard
                seekers={seekers}
                recruiters={recruiters}

                seekerPage={seekerPage}
                seekerTotalPages={seekerTotalPages}
                seekerTotal={seekerTotal}
                onSeekerPageChange={handleSeekerPageChange}

                recruiterPage={recruiterPage}
                recruiterTotalPages={recruiterTotalPages}
                onRecruiterPageChange={handleRecruiterPageChange}
                recruiterTotal={recruiterTotal}
                updateSeekerStatus={updateSeekerStatus}
                updateRecruiterStatus={updateRecruiterStatus}
                logout={handleAdminLogout}
                refreshData={refreshData}
                loading={loading}
                onSeekerFilter={handleSeekerFilter}
                onRecruiterFilter={handleRecruiterFilter}
                seekerFilter={seekerFilter}
                recruiterFilter={recruiterFilter}
              />
            </div>
          </ProtectedAdminRoute>
        } />

        <Route path="/admin/recruiters" element={
          <ProtectedAdminRoute>
            <div className={theme === 'dark' ? 'dark' : ''}>
              <AdminDashboard
                seekers={seekers}
                recruiters={recruiters}

                seekerPage={seekerPage}
                seekerTotalPages={seekerTotalPages}
                seekerTotal={seekerTotal}
                onSeekerPageChange={handleSeekerPageChange}

                recruiterPage={recruiterPage}
                recruiterTotalPages={recruiterTotalPages}
                recruiterTotal={recruiterTotal}
                onRecruiterPageChange={handleRecruiterPageChange}
                updateSeekerStatus={updateSeekerStatus}
                updateRecruiterStatus={updateRecruiterStatus}
                logout={handleAdminLogout}
                refreshData={() => {
                  fetchSeekers();
                  fetchRecruiters();
                }}
                loading={loading}
                onSeekerFilter={handleSeekerFilter}
                onRecruiterFilter={handleRecruiterFilter}
                seekerFilter={seekerFilter}
                recruiterFilter={recruiterFilter}
              />
            </div>
          </ProtectedAdminRoute>
        } />
        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;